// WHERE EVERY PART IS AT A GIVEN MOMENT OF A GIVEN STEP, and nothing else.
//
// A pure function of (step, t). No timers, no tweens, no three.js — the whole module is
// arithmetic on numbers, so it can be read, tested and reasoned about without a canvas.
// That is not tidiness: it is what makes the viewer scrubbable. A fire-and-forget tween
// ("slide the washer over 1.2s") owns the clock, and once the clock is inside the
// animation you cannot ask it where things were at 40% of step 3 — you can only play it
// and wait. Ask instead for the poses AT a moment and playing is a loop over moments,
// scrubbing is a slider over the same moments, reversing is reading them backwards, and
// jumping to step 5 costs nothing. One function, four behaviours, all of them exact.
//
// THE PARTS FORM A TREE, and that is the reason this is not a list of tweens over world
// positions. A spring washer seats on a screw and then travels WITH that screw as it
// drives home. Two independent tweens cannot say that: the washer's would have to know
// the screw's whole future path and duplicate it, and the day the screw's depth changes
// they disagree and the washer sinks through the head. Said as a tree — the washer's pose
// is relative to the screw, whatever the screw is doing — it is true by construction.
//
// The tree is not invented here either. It is object parenting in the .blend, which the
// add-on already preserves across a re-import along with each object's transform.

// x, y, z. Millimetres, in whatever frame the field says.
export type Vec3 = readonly [number, number, number];
// x, y, z, w — the same order three.js uses, so handing one over is a spread.
export type Quat = readonly [number, number, number, number];

// A rigid placement: where, and turned which way. No scale, deliberately. A part in an
// assembly is the size it is; a scale here would be a modelling mistake given somewhere it
// cannot be seen, and dropping it makes composition four lines instead of a matrix decompose.
export type Pose = { pos: Vec3; rot: Quat };

export const REST: Pose = { pos: [0, 0, 0], rot: [0, 0, 0, 1] };

// ONE PART, IN ONE PLACE, IN ONE ASSEMBLY.
//
// Not a part in the catalogue: `itemId` says which catalogue part this is, and four screws
// in a chair are four instances of one item. That distinction is the reason none of this
// can live inside the .glb — the mesh is shared by every instance and every manual that
// uses it, while everything below is true of this instance in this manual alone.
export type Instance = {
  // Unique within the assembly. The add-on will mint these; the fixture writes them by hand.
  id: string;
  // The catalogue part (`noticeia_item_id` on the Blender object).
  itemId: string;
  // What this seats ONTO. null for the one part everything else hangs off — the panel, the
  // frame, whatever the manual starts from.
  parent: string | null;
  // Where it ends up, RELATIVE TO ITS PARENT. Straight out of Blender: the object's
  // transform expressed in its parent's frame.
  seat: Pose;
  // Which way it comes in from, as a unit vector IN THE PARENT'S FRAME — the same frame
  // `seat.pos` is written in, so the start position is `seat.pos + axis * distance` with no
  // conversion in between. For a washer going onto a screw this is the screw's shank axis,
  // which is why the parent's frame is the right one to say it in.
  axis: Vec3;
  // Millimetres of straight-line approach before anything engages. The part is drawn this
  // far out along `axis` at the start of its move.
  approach: number;
  // AND THEN IT TURNS ITS WAY IN. Present only for a threaded fastener, absent for
  // everything that just slides into place.
  //
  // This is the case that settles the whole "Blender or code" question, because it is the
  // one nobody wants to keyframe: rotation is not decoration on top of the travel, it IS
  // the travel — one turn advances the screw by exactly its pitch, and a keyframed
  // approximation of that either floats or drills. Two numbers here; the coupling below.
  thread?: {
    // How deep it drives, in mm.
    depth: number;
    // Millimetres advanced per full turn. 1.0 for an M6 coarse thread.
    pitch: number;
  };
  // OR IT IS TURNED INTO PLACE. Present only on a placing; never beside `thread`, which is
  // the same word about the opposite thing — a thread is a joint being made, and a placing
  // has nothing to join to. See Turn.
  turn?: Turn;
  // Which assembly step puts it there. Everything with a lower number is already standing
  // when this step opens; everything higher is not drawn at all.
  step: number;
  // The order WITHIN that step. The washer goes on the screw before the screw goes in the
  // panel, and both of those are step 4 of the manual. Parts sharing an order move together.
  order: number;
  // HOW MUCH OF ITS SLICE IT WAITS before it moves, 0..1 — the time a tool takes to come over
  // from the copy before (see `hops`). Absent: it moves the moment its slice opens.
  waits?: number;
  // THE INSTANCES IT RIDES, ONE AFTER ANOTHER, instead of one parent for good: one screwdriver
  // turning two barrel nuts in, the first and then the second. It rides each through that one's
  // slice of the step (at `seat`, in that one's frame), and at the start of the next slice, while
  // that one `waits`, it backs out of the last and crosses to the next. `parent` is the first.
  hops?: string[];
  // AND HOW FAR OFF EACH OF THEM IT STANDS BEFORE IT SEATS, in mm, out along that one's way in:
  // the key held clear of the screw's head and pushed into it as that screw's turn opens, before
  // the screw moves. Absent: it is in place from the first frame.
  mate?: number;
  // AND WHEN IT IS DONE: once its round is over (the `after`-th slice of the step), it backs out of
  // the last one it is on, by `mate`, over the first `within` of the next slice, and is put down —
  // not drawn from then on. The screwdriver leaving the barrel nuts before the rail they are in
  // goes against the frame. Absent: it stays in the last one.
  // NO `after`: once the step's last slice is over, in a beat of `within` of a slice added to the
  // end of the step for it. The Allen key left standing in the last screw when the clip stopped.
  leaves?: { after?: number; within: number };
  // AND WHEN IT COMES IN: not drawn before the `enters`-th slice of the step opens (the
  // screwdriver for the barrel nut put in after the dowels). Absent: from the first frame.
  enters?: number;
  // MESHES OF ITS OWN THAT COME HOME BEFORE IT MOVES: the washer of a prepared screw, slid onto
  // the screw in the hand, then the screw put in. Each is the `index`-th mesh of this part (a
  // piece, see ScenePart.pieces), drawn `travel` mm out along `axis` — both in the piece's own
  // frame, the one its meshes are listed in — and brought in through its own slice (`order`).
  inner?: { index: number; axis: Vec3; travel: number; order: number }[];
  // DRAWN IN A MIRROR (see mirrorAssembly): a thread turns the other way, as a right-hand screw's
  // reflection is a left-hand one.
  mirrored?: boolean;
};

// `intro`: SLICES OF STILLNESS BEFORE THE FIRST, the parts standing where they start (the very first
// sub-step's, while the camera comes in from the whole view; see Fitting.opensWide).
export type Assembly = { instances: Instance[]; intro?: number };

// AN ASSEMBLY REFLECTED LEFT TO RIGHT, in x: the right rail's gesture drawn from the left's (see
// Fitting.mirrorAll). Each instance's pose is the reflected one (position x negated, rotation
// conjugated by the reflection), so the whole tree comes out reflected; the meshes are reflected
// by the scene (see ScenePart.mirror). A rotation's axis is a pseudo-vector: (x, −y, −z).
const mirrorX = (v: Vec3): Vec3 => [-v[0], v[1], v[2]];
const mirrorQ = (q: Quat): Quat => [q[0], -q[1], -q[2], q[3]];
export const mirrorAssembly = (asm: Assembly): Assembly => ({
  ...asm,
  instances: asm.instances.map((i) => ({
    ...i,
    seat: { pos: mirrorX(i.seat.pos), rot: mirrorQ(i.seat.rot) },
    axis: mirrorX(i.axis),
    ...(i.turn ? { turn: { axis: [i.turn.axis[0], -i.turn.axis[1], -i.turn.axis[2]] as Vec3, degrees: i.turn.degrees } } : {}),
    ...(i.inner ? { inner: i.inner.map((m) => ({ ...m, axis: mirrorX(m.axis) })) } : {}),
    mirrored: !i.mirrored,
  })),
});

// A QUARTER TURN OF THE WRIST, for the part that is joined to nothing.
//
// A fitting's only rotation is the thread's, and it is not free: the turn IS the travel. A
// placing's is the opposite — "lay the backrest frame (A) flat" turns the part and takes it
// nowhere, and there is no distance to back an angle out of. So this is stated rather than
// derived: the part starts `degrees` away from its seat about `axis` and unwinds to the
// seat, over the same eased slice the translation uses.
//
// Written in the part's OWN frame, which for a placing is the assembly's — a part joined to
// nothing has no parent whose frame to borrow.
export type Turn = { axis: Vec3; degrees: number };

// One part on screen, with the pose to draw it at.
export type Placement = {
  instance: Instance;
  // In the ASSEMBLY's frame — the tree already composed, so a viewer sets this on a flat
  // list of objects and never re-parents anything.
  pose: Pose;
  // Whether this is the part the step is currently about, for whoever wants to say so on
  // screen. True only while it is actually travelling: a part waiting its turn is not
  // moving, and a part that has arrived is not either.
  moving: boolean;
  // Where its own meshes stand off from home at this moment (see Instance.inner), in the frame
  // they are listed in. Absent when it has none.
  inner?: { index: number; offset: Vec3 }[];
  // HOW FAR IT STILL HAS TO GO along its axis, in millimetres: 0 once home. What tells a pointed
  // screw's head reaching the wood (see ClipPart.driven).
  left: number;
};

// --- arithmetic ------------------------------------------------------------------

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

// Leaves and arrives at rest. A linear move stops dead on its last frame, which reads as a
// bump — and on a fastener specifically it reads as the thing being hammered rather than
// driven.
// Exported because the CAMERA rides the same curve. A shot that ramped linearly while the
// part it is watching eased would drift against it — the two would be furthest apart exactly
// in the middle of the move, which is the part anybody is looking at.
export const ease = (k: number) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);

const unit = (v: Vec3): Vec3 => {
  const len = Math.hypot(v[0], v[1], v[2]);
  return len > 1e-9 ? [v[0] / len, v[1] / len, v[2] / len] : [0, 0, 1];
};

// Hamilton product. `qmul(a, b)` applied to a vector is a(b(v)) — b turns first. Which is
// the order composition below needs: the child's own rotation happens inside the parent's.
function qmul(a: Quat, b: Quat): Quat {
  const [ax, ay, az, aw] = a;
  const [bx, by, bz, bw] = b;
  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
}

export function qAxisAngle(axis: Vec3, angle: number): Quat {
  const [x, y, z] = unit(axis);
  const h = angle / 2;
  const s = Math.sin(h);
  return [x * s, y * s, z * s, Math.cos(h)];
}

// A stored turn as the rotation it IS, which is the only form two of them can be compared in:
// −90° about x and +90° about −x are one movement written two ways, and so are 180° about y
// and 180° about −y. Exported because that comparison is made outside this file — see
// gestures.sameTurn, which decides whether a stored turn is a gesture the selector can name,
// and would otherwise be deciding it on spelling.
export const quatOfTurn = (turn: Turn): Quat =>
  qAxisAngle(turn.axis, (turn.degrees * Math.PI) / 180);

// AND BACK OUT OF ONE: the axis and the angle a quaternion turns through.
//
// The inverse of qAxisAngle, for the write path — a start orientation typed into the movement
// bar is an ORIENTATION, and what has to be stored is the turn that reaches it from the seat.
// Null for a rotation of nothing, which is not a turn at all and is stored as the absence of
// one: an axis-angle of zero degrees carries an arbitrary axis, and writing one down would be
// a direction nobody chose sitting in the data waiting to be read as meaningful.
export function turnOfQuat(q: Quat): Turn | null {
  // Sign-normalised first: q and −q are the same rotation, and only one of them reports an
  // angle under half a turn.
  const [x, y, z, w] = q[3] < 0 ? [-q[0], -q[1], -q[2], -q[3]] : q;
  const sin = Math.sqrt(x * x + y * y + z * z);
  if (sin < 1e-9) return null;
  const angle = 2 * Math.atan2(sin, Math.min(1, Math.max(-1, w)));
  const degrees = Math.round(((angle * 180) / Math.PI) * 100) / 100;
  if (!degrees) return null;
  return { axis: [x / sin, y / sin, z / sin], degrees };
}

// Exported for the panel's write path, which has to undo exactly what `compose` below does:
// a seat is measured in the fixed part's frame, so a place typed in the ASSEMBLY's frame is
// that place brought back through however the fixed part is turned. See AssemblyPhase.
export function qRotate(q: Quat, v: Vec3): Vec3 {
  const [qx, qy, qz, qw] = q;
  const [vx, vy, vz] = v;
  // t = 2 * (q.xyz × v), then v + qw*t + q.xyz × t.
  const tx = 2 * (qy * vz - qz * vy);
  const ty = 2 * (qz * vx - qx * vz);
  const tz = 2 * (qx * vy - qy * vx);
  return [
    vx + qw * tx + qy * tz - qz * ty,
    vy + qw * ty + qz * tx - qx * tz,
    vz + qw * tz + qx * ty - qy * tx,
  ];
}

// Shortest-path spherical interpolation between two orientations.
export function qslerp(a: Quat, b: Quat, k: number): Quat {
  let [bx, by, bz, bw] = b;
  let dot = a[0] * bx + a[1] * by + a[2] * bz + a[3] * bw;
  if (dot < 0) { bx = -bx; by = -by; bz = -bz; bw = -bw; dot = -dot; }
  if (dot > 0.9995) {
    const q = [a[0] + (bx - a[0]) * k, a[1] + (by - a[1]) * k, a[2] + (bz - a[2]) * k, a[3] + (bw - a[3]) * k];
    const n = Math.hypot(q[0], q[1], q[2], q[3]);
    return [q[0] / n, q[1] / n, q[2] / n, q[3] / n];
  }
  const th = Math.acos(dot);
  const sa = Math.sin((1 - k) * th) / Math.sin(th);
  const sb = Math.sin(k * th) / Math.sin(th);
  return [a[0] * sa + bx * sb, a[1] * sa + by * sb, a[2] * sa + bz * sb, a[3] * sa + bw * sb];
}

// The child's pose, said in the grandparent's frame. This one line is what makes the
// washer ride the screw.
function compose(parent: Pose, local: Pose): Pose {
  const turned = qRotate(parent.rot, local.pos);
  return {
    pos: [parent.pos[0] + turned[0], parent.pos[1] + turned[1], parent.pos[2] + turned[2]],
    rot: qmul(parent.rot, local.rot),
  };
}

// --- the move --------------------------------------------------------------------

// How far out along `axis` a part sits before its move begins.
// EULER DEGREES IN, QUATERNION OUT — the 'XYZ' order three.js uses, digit for digit, because
// the scene reads an orientation back out with THREE.Euler and the two have to be the same
// convention or a number typed into the panel comes back as a different one.
//
// Degrees at the edge and radians inside, for the reason millimetres are at the edge and the
// panel speaks centimetres: the person types the unit they think in.
export function quatFromEulerDeg(deg: Vec3): Quat {
  const [x, y, z] = deg.map((d) => (d * Math.PI) / 360) as unknown as [number, number, number];
  const c1 = Math.cos(x); const c2 = Math.cos(y); const c3 = Math.cos(z);
  const s1 = Math.sin(x); const s2 = Math.sin(y); const s3 = Math.sin(z);
  return [
    s1 * c2 * c3 + c1 * s2 * s3,
    c1 * s2 * c3 - s1 * c2 * s3,
    c1 * c2 * s3 + s1 * s2 * c3,
    c1 * c2 * c3 - s1 * s2 * s3,
  ];
}

// AND BACK OUT AGAIN — the exact inverse of the above, three.js's `Euler.setFromQuaternion`
// in 'XYZ', through the same rotation matrix it builds. Written out rather than reached for
// through THREE because this file is the arithmetic and holds no scene: the viewer imports
// three, the phase imports this, and a panel that had to instantiate a Quaternion to read a
// number back would drag the whole library into the page that only wanted three digits.
//
// The pairing is the point. `quatFromEulerDeg` is what a typed orientation becomes and this
// is what a computed one reads as, so what you type into the panel and what the panel shows
// you a moment later have to be the same number — which they are only if both agree on the
// order the three turns are applied in.
export function eulerDegFromQuat(q: Quat): Vec3 {
  const [x, y, z, w] = q;
  const x2 = x + x; const y2 = y + y; const z2 = z + z;
  const xx = x * x2; const xy = x * y2; const xz = x * z2;
  const yy = y * y2; const yz = y * z2; const zz = z * z2;
  const wx = w * x2; const wy = w * y2; const wz = w * z2;
  const m11 = 1 - (yy + zz); const m12 = xy - wz; const m13 = xz + wy;
  const m22 = 1 - (xx + zz); const m23 = yz - wx;
  const m32 = yz + wx; const m33 = 1 - (xx + yy);
  const clamped = Math.min(1, Math.max(-1, m13));
  let ex: number;
  let ez: number;
  const ey = Math.asin(clamped);
  // GIMBAL LOCK, and the branch is not a nicety: a quarter turn about Y — which is one of the
  // five gestures — puts m13 at 1, where the other two angles stop being separable and
  // atan2(-m23, m33) is atan2 of two zeros. Three's own answer is to give the whole of it to
  // X and leave Z at nothing, which is the reading that comes back as the same quaternion.
  if (Math.abs(m13) < 0.9999999) {
    ex = Math.atan2(-m23, m33);
    ez = Math.atan2(-m12, m11);
  } else {
    ex = Math.atan2(m32, m22);
    ez = 0;
  }
  return [(ex * 180) / Math.PI, (ey * 180) / Math.PI, (ez * 180) / Math.PI];
}

// The conjugate, which is the inverse for the unit quaternions this file deals in.
export const qInvert = (q: Quat): Quat => [-q[0], -q[1], -q[2], q[3]];

// Exported for the one caller that has to express one orientation in another's frame; see
// assemblyOf, where the moving part's seat is written in the fixed part's.
export const qCompose = (a: Quat, b: Quat): Quat => qmul(a, b);

export const startOffset = (inst: Instance) => inst.approach + (inst.thread?.depth ?? 0);

// One part's pose relative to its parent, `k` of the way through its own move.
//
// `k` is this INSTANCE's progress, not the step's — the step hands out slices of itself,
// see below. At 0 the part hangs at its full offset (which is what an exploded diagram is);
// at 1 it is exactly `seat`, so a finished step is pixel-identical to a static render of it.
function localPose(inst: Instance, k: number): Pose {
  const total = startOffset(inst);
  // A part that turns has a move even with nowhere to travel — "turn the seat over" is a
  // whole sub-step and not a millimetre of it. Only a part that neither travels nor turns
  // is already where it belongs at every k.
  if (total <= 0 && !inst.turn) return inst.seat;

  const axis = unit(inst.axis);
  // Distance still to go.
  const left = total * (1 - ease(k));
  const pos: Vec3 = [
    inst.seat.pos[0] + axis[0] * left,
    inst.seat.pos[1] + axis[1] * left,
    inst.seat.pos[2] + axis[2] * left,
  ];

  // THE GESTURE. Stated, not coupled: there is no distance to divide by a pitch, so the
  // angle runs off the same eased k the position does — full at the start, nothing left at
  // the seat. The two cannot drift for the same reason the thread cannot: both are read off
  // one t rather than tweened beside each other.
  if (inst.turn) {
    const angle = (1 - ease(k)) * inst.turn.degrees * (Math.PI / 180);
    return { pos, rot: qmul(qAxisAngle(inst.turn.axis, angle), inst.seat.rot) };
  }

  // THE COUPLING. Once the part is within `depth` of home the threads have bitten, and from
  // there the turn is not a choice: the remaining travel divided by the pitch IS the number
  // of turns left. Backing the angle out of the distance rather than tweening it separately
  // means the two can never drift, at any playback rate, at any scrub position.
  //
  // Sign: A RIGHT-HAND THREAD, clockwise seen from its head going in. `axis` points out of the
  // hole, toward the head; clockwise seen from there is a negative turn about it, so the angle
  // still to go is positive and runs down to zero as it seats. Checked on a screw's model: turned
  // this way its crests stand still in the world as it advances, as a thread biting wood does —
  // the other way they raced at twice its speed. The spin is about `axis`, and the part's offset
  // is along that same axis, so spinning it leaves the position alone.
  if (!inst.thread || left > inst.thread.depth) return { pos, rot: inst.seat.rot };
  const turns = left / inst.thread.pitch;
  return { pos, rot: qmul(qAxisAngle(axis, (inst.mirrored ? -1 : 1) * turns * Math.PI * 2), inst.seat.rot) };
}

// The steps this assembly has, in order.
export const stepsOf = (asm: Assembly): number[] =>
  [...new Set(asm.instances.map((i) => i.step))].sort((a, b) => a - b);

// HOW ONE STEP'S PROGRESS IS SHARED OUT AMONG ITS PARTS.
//
// A step is a sequence, not a chord: the washer goes on the screw, THEN the screw goes in.
// So the step's own 0..1 is cut into one slice per distinct `order`, in ascending order,
// and each part is given its slice.
//
// The part of this that matters is what a part does OUTSIDE its slice. Before it, `k` is 0
// — the part is on screen, hanging at its full offset, waiting. That is not a placeholder,
// it is the picture an instruction manual actually draws: the washer slides onto a screw
// that is visibly still hovering above its hole. After it, `k` is 1 and the part is seated,
// so anything that seated onto it is carried along by the tree without being told.
function progressOf(orders: number[], order: number, t: number): number {
  const k = orders.indexOf(order);
  if (k < 0) return 1;
  const slot = 1 / orders.length;
  return clamp01((t - k * slot) / slot);
}

// The share of a wait a tool spends crossing from one copy to the next; the rest it spends
// seating itself in the next (see Instance.mate).
const CROSS = 0.6;

// AND THE SAME, LESS WHAT THE PART WAITS AT THE START OF ITS SLICE (see Instance.waits).
const progressOfInst = (orders: number[], inst: Instance, t: number): number => {
  const k = progressOf(orders, inst.order, t);
  const waits = inst.waits ?? 0;
  return waits > 0 ? clamp01((k - waits) / (1 - waits)) : k;
};

// EVERY PART ON SCREEN AT MOMENT `t` OF STEP `step`, already composed into assembly space.
//
// Parts belonging to a later step are absent from the result rather than returned hidden:
// what a step shows is what it has reached, and a viewer that draws exactly what it is
// given cannot leave a ghost behind.
//
// `t` is the step's own 0..1. Where it comes from is the caller's business, and that is the
// point — a play loop, a scrubber, a scroll position and a screenshot script are all just
// different ways of choosing a number.
export function poseAt(asm: Assembly, step: number, t: number): Placement[] {
  const now = clamp01(t);
  const here = asm.instances.filter((i) => i.step <= step);
  const byId = new Map(here.map((i) => [i.id, i]));

  // The orders this step actually uses, so a step numbered 1, 4, 9 shares itself out in
  // three equal slices rather than nine with six of them empty.
  const orders = [...new Set([
    ...here.filter((i) => i.step === step).map((i) => i.order),
    ...here.flatMap((i) => (i.inner ?? []).map((m) => m.order)),
  ])].sort((a, b) => a - b);

  // Walked with memoisation, because a chain of five parts would otherwise recompute the
  // root five times — and because it is where a cycle in the data would otherwise hang the
  // render loop rather than dropping one part.
  // THE STEP'S CLOCK, in slices: the slices its orders take, then a closing beat where a tool is
  // put down after the last one (see Instance.leaves). The parts are done when the slices are.
  // Any instance standing in the step, a tool's being step 0 (it is in the picture from the start).
  const outro = Math.max(0, ...here.filter((i) => i.leaves && i.leaves.after == null)
    .map((i) => i.leaves!.within));
  const intro = asm.intro ?? 0;
  const clock = Math.max(0, now * (orders.length + outro + intro) - intro);
  const moved = orders.length ? Math.min(1, clock / orders.length) : now;

  const world = new Map<string, Pose | null>();
  const solve = (inst: Instance, seen: Set<string>): Pose | null => {
    const cached = world.get(inst.id);
    if (cached !== undefined) return cached;
    // A part whose parent is not standing cannot be standing either: it would be floating
    // at a pose measured from something that is not there. Same answer for a cycle.
    if (seen.has(inst.id)) return null;
    seen.add(inst.id);

    const k = inst.step === step ? progressOfInst(orders, inst, moved) : 1;
    const local = localPose(inst, k);
    // Where `local` puts this instance on the instance with this id, as things stand now.
    const on = (id: string, at: Pose): Pose | null => {
      const parent = byId.get(id);
      const above = parent ? solve(parent, seen) : null;
      return above ? compose(above, at) : null;
    };

    let pose: Pose | null;
    if (inst.enters != null && clock < inst.enters) {
      pose = null;
    } else if (inst.hops?.length) {
      // WHICH ONE IT IS ON: the last whose slice has opened (the first, before any has).
      const hops = inst.hops.map((id) => byId.get(id)).filter((h): h is Instance => !!h);
      const opened = (h: Instance) => {
        const i = orders.indexOf(h.order);
        return i < 0 ? 1 : clock - i;
      };
      let n = 0;
      for (let i = 1; i < hops.length; i++) if (hops[i].step === step && opened(hops[i]) >= 0) n = i;
      const here = hops[n] ? on(hops[n].id, local) : null;
      // While the one it is on WAITS (see Instance.waits), the tool gets to it: from the last one,
      // out the way that one came in, over in an arc so it is not dragged through the part (the
      // first CROSS of the wait), then into this one along its way in, from `mate` mm off (the rest).
      // Before its turn opens at all, it stands off by `mate`.
      const into = hops[n]?.waits && hops[n].step === step ? opened(hops[n]) / hops[n].waits! : 1;
      const wayIn = (h: Instance): Vec3 => {
        const parent = h.parent ? byId.get(h.parent) : undefined;
        const frame = parent ? solve(parent, seen) : null;
        return qRotate(frame?.rot ?? [0, 0, 0, 1], unit(h.axis));
      };
      const mate = inst.mate ?? 0;
      const crosses = n > 0;
      const cross = crosses ? CROSS : 0;
      if (here && into < 1) {
        const out = wayIn(hops[n]);
        const off = (k: number): Pose => ({
          pos: [0, 1, 2].map((a) => here.pos[a] + out[a] * mate * (1 - ease(k))) as unknown as Vec3,
          rot: here.rot,
        });
        const from = crosses ? on(hops[n - 1].id, local) : null;
        if (from && into < cross) {
          const stand = off(0);
          const e = ease(Math.max(0, into) / cross);
          const back = wayIn(hops[n - 1]);
          const lift = startOffset(hops[n - 1]) * Math.sin(Math.PI * e);
          pose = {
            pos: [0, 1, 2].map((a) => from.pos[a] + (stand.pos[a] - from.pos[a]) * e + back[a] * lift) as unknown as Vec3,
            rot: qslerp(from.rot, stand.rot, e),
          };
        } else {
          pose = off(Math.max(0, (into - cross) / (1 - cross)));
        }
      } else {
        pose = here;
      }
      // PUT DOWN, its round over (see Instance.leaves).
      const gone = inst.leaves ? clock - (inst.leaves.after ?? orders.length) : -1;
      if (pose && here && inst.leaves && gone > 0) {
        const out = wayIn(hops[n]);
        const e = ease(Math.min(1, gone / inst.leaves.within));
        pose = gone >= inst.leaves.within ? null : {
          pos: [0, 1, 2].map((a) => here.pos[a] + out[a] * mate * e) as unknown as Vec3,
          rot: here.rot,
        };
      }
    } else if (inst.parent === null) {
      pose = local;
    } else {
      const parent = byId.get(inst.parent);
      const above = parent ? solve(parent, seen) : null;
      pose = above ? compose(above, local) : null;
    }
    world.set(inst.id, pose);
    return pose;
  };

  const out: Placement[] = [];
  for (const inst of here) {
    const pose = solve(inst, new Set());
    if (!pose) continue;
    const k = inst.step === step ? progressOfInst(orders, inst, moved) : 1;
    // A part with nowhere to travel AND nothing to turn is never the part that is moving,
    // however much of its slice has gone by — the base an assembly starts from has a step of
    // its own and does not budge during it, and highlighting it would answer "what is this
    // step doing?" with the one thing on screen that is doing nothing. A part being turned
    // over on the spot is doing the step, and travels not one millimetre while it does.
    const travels = startOffset(inst) > 0 || !!inst.turn;
    const inner = inst.inner?.map((m) => {
      const left = m.travel * (1 - ease(progressOf(orders, m.order, moved)));
      const axis = unit(m.axis);
      return { index: m.index, offset: [axis[0] * left, axis[1] * left, axis[2] * left] as Vec3 };
    });
    out.push({
      instance: inst, pose, moving: travels && inst.step === step && k > 0 && k < 1,
      left: startOffset(inst) * (1 - ease(k)),
      ...(inner ? { inner } : {}),
    });
  }
  return out;
}
